import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, ShieldAlert, TrendingUp, ChevronRight, Shield } from 'lucide-react';
import { usePlanEntitlement } from '../../hooks/usePlanEntitlement';
import { useCheckout } from '../../hooks/useCheckout';
import { PLAN_PRICES_USD, formatUsd } from '../../data/pricingPlans';
import { PlanOptionCards } from '../Billing/PlanOptionCards';
interface UnlockScreenProps {
  /** Completes onboarding. Any pasted text is carried into the scan. */
  onUnlock: (pastedText?: string) => void;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export const UnlockScreen: React.FC<UnlockScreenProps> = ({
  onUnlock
}) => {
  const [pastedText, setPastedText] = useState('');
  const [showPlans, setShowPlans] = useState(false);
  const {
    planId,
    accessLabel,
    freeScanAvailable,
    canScan,
    loading
  } = usePlanEntitlement();
  const {
    onCheckout,
    pendingPlan
  } = useCheckout('onboarding');
  const features = [{
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    title: 'Manipulation Patterns',
    description: 'Detects psychological warfare tactics'
  }, {
    icon: ShieldAlert,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    title: 'Gaslighting Detection',
    description: 'Exposes reality distortion attempts'
  }, {
    icon: TrendingUp,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
    title: 'Toxicity Score',
    description: 'Calculates relationship danger level'
  }];
  // A user who can still scan starts scanning. Everyone else buys the
  // cheapest full scan — never pushed straight into a subscription.
  const canStartScan = canScan || freeScanAvailable;
  return <motion.div className="w-full max-w-md mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-1578" data-mp="10v0al3853jj4">
      <motion.div className="text-center mb-8" variants={itemVariants} data-id="element-1579" data-mp="w2h1aighjak4">
        <div className="inline-flex items-center gap-2 bg-[#FF0000]/10 text-[#FF0000] px-4 py-1.5 border border-[#FF0000]/20 mb-6 backdrop-blur-sm" data-id="element-1580" data-mp="1ard6m7r5q9h1">
          <span className="relative flex h-2 w-2" data-id="element-1581" data-mp="1jtcnn0269djz">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF0000] opacity-75" data-id="element-1582" data-mp="1js1mz940wlx3"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF0000]" data-id="element-1583" data-mp="1fsj3mhruoiwd"></span>
          </span>
          <span className="font-mono text-[10px] tracking-widest uppercase font-bold" data-id="element-1584" data-mp="3lmshe5tk38j6">
            Forensic Engine Ready
          </span>
        </div>

        <h2 className="text-3xl font-bold mb-3 leading-tight text-white tracking-tight" data-id="element-1585" data-mp="37g8ncg20w8qe">
          Profile <span className="text-[#FF0000]" data-id="element-1586" data-mp="3bh10vlhxgzsh">Complete</span>.
        </h2>
        <p className="text-sm text-gray-400 leading-relaxed max-w-xs mx-auto font-mono" data-id="element-1587" data-mp="2xpmdl2gzmcfm">
          Paste a conversation to run your first forensic scan and surface the
          patterns hiding in it.
        </p>
      </motion.div>

      <motion.div className="mb-8 space-y-3" variants={itemVariants} data-id="element-1588" data-mp="2nq58am5ne0k6">
        {features.map((feature, index) => <motion.div key={index} className={`flex items-center text-left bg-white/5 p-4 border ${feature.border} hover:bg-white/10 transition-colors group relative overflow-hidden`} variants={itemVariants} data-id="element-1589" data-mp="2sruo0f3tf2el" data-mpi={index}>
            <div className={`p-2 mr-3 ${feature.bg} border ${feature.border}`} data-id="element-1590" data-mp="czextfiofo8o">
              <feature.icon size={18} className={feature.color} strokeWidth={2} data-id="element-1591" data-mp="hy89r3ibf76q" />
            </div>
            <div className="flex-1" data-id="element-1592" data-mp="2e0uvkvwvhww6">
              <h4 className="font-bold text-sm mb-0.5 text-white tracking-wide" data-id="element-1593" data-mp="292a5hpbc4tlg">
                {feature.title}
              </h4>
              <p className="text-gray-500 text-xs font-mono" data-id="element-1594" data-mp="242fe7knne1zv">
                {feature.description}
              </p>
            </div>
          </motion.div>)}
      </motion.div>

      {/* Scanner Widget */}
      <motion.div className="relative mb-6 group" variants={itemVariants} data-id="element-1595" data-mp="1v3ai0kzk6kqv">
        <div className="relative bg-[#0a0a0a] border border-[#FF0000]/30 overflow-hidden group-hover:border-[#FF0000]/50 transition-colors duration-300" data-id="element-1596" data-mp="1qbae6hgdb7p0">
          {/* Corner Markers */}
          <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#FF0000] z-20" data-id="element-1597" data-mp="1gdw7h9k8xx9w" />
          <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#FF0000] z-20" data-id="element-1598" data-mp="1ccizotxlfk0c" />
          <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#FF0000] z-20" data-id="element-1599" data-mp="17cejz64ietb6" />
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#FF0000] z-20" data-id="element-1600" data-mp="3j12vm33n0yyq" />

          <div className="p-6 text-left relative overflow-hidden" data-id="element-1601" data-mp="3nybg4ac9df35">
            {/* SCANNING LINE ANIMATION */}
            <motion.div className="absolute top-0 left-0 w-full h-[2px] bg-[#FF0000] shadow-[0_0_20px_#FF0000] z-10" animate={{
            top: ['0%', '100%', '0%']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1602" data-mp="3s29dafi0wftk" />

            {/* Grid Overlay */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,0,0,0.03)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-1603" data-mp="398x4swp4gnl4" />

            <div className="relative z-20" data-id="element-1604" data-mp="2glj3yniaz7vq">
              <label htmlFor="onboarding-conversation" className="sr-only" data-id="element-1605" data-mp="2lkm7g3imx61l">
                Paste your conversation
              </label>
              <textarea id="onboarding-conversation" value={pastedText} onChange={e => setPastedText(e.target.value)} className="w-full h-32 bg-[#050505] border border-gray-800 focus:border-[#FF0000] focus:ring-1 focus:ring-[#FF0000] text-white p-4 font-mono text-sm resize-none transition-colors" placeholder="Paste your chat conversation here (e.g., texts, emails, or messages) to begin your forensic analysis..." data-id="element-1606" data-mp="3jwq6edkwdmyr" />
              <p className="text-[11px] text-gray-500 font-mono mt-3" data-id="element-1607" data-mp="3ey566e5nb3ab">
                Copy the messages from your chat app and paste them here.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Current scan access */}
      <motion.div variants={itemVariants} className="mb-5 flex items-center gap-3 border border-gray-800 bg-[#0a0a0a] p-4" data-id="element-1608" data-mp="u9hhlcuj4ajx">
        <Shield size={18} className="shrink-0 text-gray-500" data-id="element-1609" data-mp="xp2huiyr95lm" />
        <div className="min-w-0" data-id="element-1610" data-mp="xron2by6b90o">
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1611" data-mp="11r5w1fct6oal">
            Current access
          </p>
          <p className="mt-1 font-mono text-sm text-white" data-id="element-1612" data-mp="nsz5yle3pslh">
            {loading ? 'Verifying clearance…' : freeScanAvailable ? '1 free scan available' : accessLabel}
          </p>
        </div>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-1613" data-mp="1ldv6dud1sb67">
        {canStartScan ? <button type="button" onClick={() => onUnlock(pastedText.trim() || undefined)} className="w-full bg-[#FF0000] py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1614" data-mp="16qa6e9bgys2i">
            Start free scan
          </button> : <button type="button" onClick={() => void onCheckout('single_scan')} disabled={pendingPlan !== null} className="w-full bg-[#FF0000] py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-[#D90000] disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1615" data-mp="43wt1iu6bacx">
            {pendingPlan === 'single_scan' ? 'Processing…' : `Buy a single scan — ${formatUsd(PLAN_PRICES_USD.single_scan)}`}
          </button>}

        <button type="button" onClick={() => setShowPlans(open => !open)} aria-expanded={showPlans} className="mt-3 w-full border border-gray-700 bg-transparent py-4 font-bold uppercase tracking-wider text-white transition-colors duration-200 ease-out hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#050505]" data-id="element-1616" data-mp="e35h5v2cxurd">
          {showPlans ? 'Hide plans' : 'View plans'}
        </button>

        <AnimatePresence initial={false} data-id="element-1617" data-mp="1e7elsupdmek0">
          {showPlans && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-1618" data-mp="1i9jgzxvh3j8r">
              <div className="pt-4" data-id="element-1619" data-mp="25zdu8gjpf9tj">
                <PlanOptionCards onCheckout={plan => void onCheckout(plan)} pendingPlan={pendingPlan} currentPlanId={planId} onStartFree={canStartScan ? () => onUnlock(pastedText.trim() || undefined) : undefined} data-id="element-1620" data-mp="25ycevzvno160" />
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Skip to Dashboard */}
        <motion.button onClick={() => onUnlock()} className="w-full mt-4 text-gray-500 text-xs font-mono uppercase tracking-widest hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 group py-3" whileHover={{
        scale: 1.01
      }} data-id="element-1621" data-mp="15xup0msb9cj3">
          Skip to Dashboard
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-1622" data-mp="1onattmb13r5b" />
        </motion.button>
      </motion.div>

      <motion.div className="mt-2 text-center" variants={itemVariants} data-id="element-1623" data-mp="1ykp9hjp4obwk">
        <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest" data-id="element-1624" data-mp="1yicjcj97uv8f">
          🔒 Secure Payment • Encrypted Session
        </p>
      </motion.div>
    </motion.div>;
};